import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import { Contact, QUERY_KEY as GET_ALL_QUERY_KEY } from './useGetAllContacts';
import { QUERY_KEY as GET_ONE_QUERY_KEY } from './useGetContact';

export interface UpdateContactDetailDto {
  id?: number; // For existing details to update
  type: string; // 'email' or 'phone'
  value: string;
  label?: string;
  is_primary?: boolean;
}

export interface ApiRequest_Update_Contact {
  name?: string;
  user_id?: string;
  role?: string;
  metadata?: Record<string, unknown>;
  details?: UpdateContactDetailDto[];
  building_ids?: number[];
  unit_ids?: number[];
  location_ids?: number[];
}

export type ApiResponse_Update_Contact = Contact;

const ENDPOINT = 'contacts';
const METHOD = 'PUT';

export const useUpdateContact = (
  contactId: number,
  stateFns?: MutationStateFns<ApiResponse_Update_Contact>
) => {
  const { session } = useAuth();
  const { selectedPropertyLocation } = usePropertyManager();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ApiRequest_Update_Contact): Promise<ApiResponse_Update_Contact> => {
      if (!selectedPropertyLocation?.id) {
        throw new Error('No location selected');
      }

      return handleApiCall<ApiResponse_Update_Contact, ApiRequest_Update_Contact>(
        session,
        METHOD,
        `${ENDPOINT}/${contactId}`,
        data,
        {
          params: {
            location_id: selectedPropertyLocation.id,
          },
        }
      );
    },
    onSuccess: data => {
      enqueueSnackbar(`Contact "${data.name}" updated successfully`, { variant: 'success' });

      // Invalidate contacts list and single contact queries
      queryClient.invalidateQueries({
        queryKey: GET_ALL_QUERY_KEY(selectedPropertyLocation?.id),
      });
      queryClient.invalidateQueries({
        queryKey: GET_ONE_QUERY_KEY(contactId, selectedPropertyLocation?.id),
      });

      stateFns?.onSuccess?.(data);
    },
    onError: (err: AxiosError) => {
      enqueueSnackbar('Failed to update contact', { variant: 'error' });
      stateFns?.onError?.(err);
    },
  });
};
